/**
 * InformationPresenter
 * --
 */
import React from 'react';
import { Content, Row, Col, Title } from 'components';
import { Button, Descriptions, Flex, Input, Select, Spin } from 'antd';
import { openAddressModal } from 'utils/DaumAPIManager';
import Formatter from 'utils/Formatter';
import ShopUploadFile from 'routes/admin.routes/admin.brand/BrandDetail/components/ShopUploadFile';

// 입금은행
const BANK_LIST = [
  '카카오뱅크',
  '국민은행',
  '기업은행',
  '농협은행',
  '신한은행',
  'iM뱅크',
  '산업은행',
  '우리은행',
  '한국씨티은행',
  '하나은행',
  'SC제일은행',
  '경남은행',
  '광주은행',
  '도이치은행',
  '뱅크오브아메리카',
  '부산은행',
  '산림조합중앙회',
  '저축은행',
  '새마을금고',
  '수협은행',
  '신협중앙회',
  '우체국',
  '전북은행',
  '제주은행',
  '중국건설은행',
  '중국공상은행',
  '중국은행',
  'BNP파리바은행',
  'HSBC은행',
  'JP모간체이스은행',
  '케이뱅크',
  '토스뱅크',
];

const InformationPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    shopId,
    shopInformation,
    mallInformation,
    storeInformation,
    shopType,
    isLoading,
    files,
    direction,
    onChange,
    onSubmit,
  } = props;

  /* ===== STATE ===== */
  // 업체 기본 정보
  const shopInfo = React.useMemo(() => {
    return [
      {
        key: 'shop_information_name',
        label: '업체명',
        children: (
          <Input
            size="large"
            value={shopInformation.shop_information_name}
            onChange={(e) =>
              onChange('SHOP', 'shop_information_name', e.target.value)
            }
            placeholder="업체명을 입력해주세요."
            count={{
              max: 50,
              show: true,
            }}
            maxLength={50}
          />
        ),
      },
      {
        key: 'shop_information_content',
        label: '업체 설명',
        children: (
          <Input.TextArea
            size="large"
            value={shopInformation.shop_information_content}
            onChange={(e) =>
              onChange('SHOP', 'shop_information_content', e.target.value)
            }
            placeholder="업체 설명을 입력해주세요."
            count={{
              max: 255,
              show: true,
            }}
            maxLength={255}
            rows={8}
          />
        ),
      },
      {
        key: 'shop_information_address',
        label: '주소',
        children: (
          <Flex vertical gap={4}>
            <Flex gap={12}>
              <Input
                size="large"
                value={shopInformation.shop_information_postcode}
                placeholder="우편번호"
                readOnly
              />
              <Button size="large" onClick={() => handleOpen()}>
                주소찾기
              </Button>
            </Flex>
            <Input
              size="large"
              value={shopInformation.shop_information_address}
              placeholder="주소"
              readOnly
            />
            <Input
              size="large"
              value={shopInformation.shop_information_address_detail}
              onChange={(e) =>
                onChange(
                  'SHOP',
                  'shop_information_address_detail',
                  e.target.value
                )
              }
              placeholder="상세주소"
              maxLength={50}
            />
          </Flex>
        ),
      },
    ];
    // eslint-disable-next-line
  }, [shopInformation]);
  // 입점업체 정보
  const mallInfo = React.useMemo(() => {
    return [
      {
        key: 'store_num',
        label: '대표번호',
        children: (
          <Input
            size="large"
            value={mallInformation.store_num}
            onChange={(e) =>
              onChange('MALL', 'store_num', Formatter.hyphen(e.target.value))
            }
            placeholder="대표번호를 입력해주세요."
            count={{
              max: 13,
              show: true,
            }}
            maxLength={13}
          />
        ),
      },
      {
        key: 'business_num',
        label: '사업자등록번호',
        children: (
          <Input
            size="large"
            value={mallInformation.business_num}
            onChange={(e) =>
              onChange(
                'MALL',
                'business_num',
                Formatter.hyphen(e.target.value, 'business_num')
              )
            }
            placeholder="사업자등록번호를 입력해주세요."
            count={{
              max: 12,
              show: true,
            }}
            maxLength={12}
          />
        ),
      },
      {
        key: 'store_owner',
        label: '대표자명',
        children: (
          <Input
            size="large"
            value={mallInformation.store_owner}
            onChange={(e) => onChange('MALL', 'store_owner', e.target.value)}
            placeholder="대표자명을 입력해주세요."
            count={{
              max: 50,
              show: true,
            }}
            maxLength={50}
          />
        ),
      },
      {
        key: 'bank_name',
        label: '입금은행',
        children: (
          <Select
            size="large"
            value={mallInformation.bank_name}
            onChange={(e) => onChange('MALL', 'bank_name', e)}
            placeholder="입금은행을 선택해주세요."
            options={BANK_LIST.map((item) => ({ label: item, value: item }))}
            style={{ width: '100%' }}
          />
        ),
      },
      {
        key: 'account_number',
        label: '입금계좌',
        children: (
          <Input
            size="large"
            value={mallInformation.account_number}
            onChange={(e) => onChange('MALL', 'account_number', e.target.value)}
            placeholder="입금계좌를 입력해주세요."
            count={{
              max: 24,
              show: true,
            }}
            maxLength={24}
          />
        ),
      },
      {
        key: 'LICENSE',
        label: '사업자등록증',
        children: (
          <ShopUploadFile
            file={files?.LICENSE}
            type={'LICENSE'}
            shopId={shopId}
            callback={(f) => onChange('FILE', 'LICENSE', f)}
          />
        ),
      },
      ...(shopInformation?.is_brand === 'Y'
        ? [
            {
              key: 'THUMBNAIL',
              label: '브랜드관 썸네일',
              children: (
                <ShopUploadFile
                  file={files?.THUMBNAIL}
                  type={'THUMBNAIL'}
                  shopId={shopId}
                  callback={(f) => onChange('FILE', 'THUMBNAIL', f)}
                />
              ),
            },
            {
              key: 'STORE',
              label: '브랜드관 상세이미지',
              children: (
                <ShopUploadFile
                  file={files?.STORE}
                  type={'STORE'}
                  shopId={shopId}
                  callback={(f) => onChange('FILE', 'STORE', f)}
                />
              ),
            },
          ]
        : []),
    ];
    // eslint-disable-next-line
  }, [
    mallInformation,
    files.LICENSE,
    files.THUMBNAIL,
    files.STORE,
    shopId,
    shopInformation.is_brand,
  ]);
  // 매장 정보
  const storeInfo = React.useMemo(() => {
    return [
      {
        key: 'store_num',
        label: '대표번호',
        children: (
          <Input
            size="large"
            value={mallInformation.store_num}
            onChange={(e) =>
              onChange('STORE', 'store_num', Formatter.hyphen(e.target.value))
            }
            placeholder="대표번호를 입력해주세요."
            count={{
              max: 13,
              show: true,
            }}
            maxLength={13}
          />
        ),
      },
      {
        key: 'business_num',
        label: '사업자등록번호',
        children: (
          <Input
            size="large"
            value={mallInformation.business_num}
            onChange={(e) =>
              onChange(
                'STORE',
                'business_num',
                Formatter.hyphen(e.target.value, 'business_num')
              )
            }
            placeholder="사업자등록번호를 입력해주세요."
            count={{
              max: 12,
              show: true,
            }}
            maxLength={12}
          />
        ),
      },
      {
        key: 'store_owner',
        label: '대표자명',
        children: (
          <Input
            size="large"
            value={mallInformation.store_owner}
            onChange={(e) => onChange('STORE', 'store_owner', e.target.value)}
            placeholder="대표자명을 입력해주세요."
            count={{
              max: 50,
              show: true,
            }}
            maxLength={50}
          />
        ),
      },
    ];
    // eslint-disable-next-line
  }, [storeInformation]);

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
  };

  /* ===== FUNCTION ===== */
  /**--------------------
   * 다음 주소 검색 Popup Open
   * --------------------
   */
  const handleOpen = () => {
    openAddressModal((res) => {
      onChange('SHOP', 'shop_information_postcode', res.zonecode);
      onChange('SHOP', 'shop_information_address', res.address);
    });
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row gutter={[24, 24]}>
            <Col x={24}>
              <Title style={{ ...styles.title }}>업체 관리</Title>
            </Col>

            <Col x={direction === 'vertical' ? 24 : 12}>
              <Content
                maxWidth={'100%'}
                padding={24}
                style={{ borderRadius: 4, height: '100%' }}
              >
                <Descriptions
                  title={'기본정보'}
                  bordered
                  items={shopInfo}
                  column={1}
                />
              </Content>
            </Col>
            <Col x={direction === 'vertical' ? 24 : 12}>
              <Content
                maxWidth={'100%'}
                padding={24}
                style={{ borderRadius: 4, height: '100%' }}
              >
                <Descriptions
                  title={
                    shopType === 'ALL' || shopType === 'SHOP'
                      ? '업체정보'
                      : '매장정보'
                  }
                  bordered
                  items={
                    shopType === 'ALL' || shopType === 'SHOP'
                      ? mallInfo
                      : storeInfo
                  }
                  column={1}
                />
              </Content>
            </Col>

            <Col x={24}>
              <Flex justify="flex-end">
                <Button type="primary" onClick={() => onSubmit()}>
                  저장하기
                </Button>
              </Flex>
            </Col>
          </Row>
        </Content>
      </Spin>
    </>
  );
};

export default InformationPresenter;
